import { isoDateTime, money, type Calendar, type ConstraintType, type Task, type TaskType } from '@bigint-project/core';
import { TaskCollaborationPanel } from '../collaboration/TaskCollaborationPanel';
import type { TaskCollaborationPort } from '../../platform/ports';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react';

export type TaskDetailsPatch = Partial<Pick<Task,
  | 'name'
  | 'taskType'
  | 'effortDriven'
  | 'isManuallyScheduled'
  | 'constraintType'
  | 'constraintDate'
  | 'deadline'
  | 'calendarId'
  | 'ignoreResourceCalendar'
  | 'priority'
  | 'fixedCost'
  | 'fixedCostAccrual'
  | 'notes'
>>;

interface TaskDetailsDialogProps {
  readonly isOpen: boolean;
  /** 唯讀宿主（雲端 viewer）：欄位全部停用、不顯示儲存，只保留檢視與協作面板。 */
  readonly readOnly?: boolean;
  readonly task: Task;
  readonly calendars: readonly Calendar[];
  /** 雲端宿主才有的留言與附件 port；undefined 代表這個宿主沒有協作功能。 */
  readonly collaboration?: TaskCollaborationPort;
  readonly onCancel: () => void;
  readonly onSave: (patch: TaskDetailsPatch) => void;
}

const CONSTRAINT_OPTIONS: readonly { readonly value: ConstraintType; readonly label: string }[] = [
  { value: 'ASAP', label: '越早越好 (ASAP)' },
  { value: 'ALAP', label: '越晚越好 (ALAP)' },
  { value: 'SNET', label: '開始不得早於 (SNET)' },
  { value: 'SNLT', label: '開始不得晚於 (SNLT)' },
  { value: 'FNET', label: '完成不得早於 (FNET)' },
  { value: 'FNLT', label: '完成不得晚於 (FNLT)' },
  { value: 'MSO', label: '必須開始於 (MSO)' },
  { value: 'MFO', label: '必須完成於 (MFO)' },
];

interface FormValues {
  name: string;
  taskType: TaskType;
  effortDriven: boolean;
  isManuallyScheduled: boolean;
  constraintType: ConstraintType;
  constraintDate: string;
  deadline: string;
  calendarId: string;
  ignoreResourceCalendar: boolean;
  priority: string;
  fixedCost: string;
  fixedCostAccrual: Task['fixedCostAccrual'];
  notes: string;
}

function toLocalInput(value: string | null): string {
  return value?.slice(0, 16) ?? '';
}

/** Domain datetimes are local wall-clock values; no UTC conversion is performed. */
function fromLocalInput(value: string): ReturnType<typeof isoDateTime> | null {
  return value === '' ? null : isoDateTime(`${value}:00`);
}

function makeFormValues(task: Task): FormValues {
  return {
    name: task.name,
    taskType: task.taskType,
    effortDriven: task.effortDriven,
    isManuallyScheduled: task.isManuallyScheduled,
    constraintType: task.constraintType,
    constraintDate: toLocalInput(task.constraintDate),
    deadline: toLocalInput(task.deadline),
    calendarId: task.calendarId ?? '',
    ignoreResourceCalendar: task.ignoreResourceCalendar,
    priority: String(task.priority),
    fixedCost: String(task.fixedCost as number),
    fixedCostAccrual: task.fixedCostAccrual,
    notes: task.notes,
  };
}

export function TaskDetailsDialog({ isOpen, readOnly = false, task, calendars, collaboration, onCancel, onSave }: TaskDetailsDialogProps): ReactElement | null {
  const [values, setValues] = useState<FormValues>(() => makeFormValues(task));
  const [errors, setErrors] = useState<Partial<Record<'name' | 'constraintDate' | 'priority' | 'fixedCost', string>>>({});
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setValues(makeFormValues(task));
      setErrors({});
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, task]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nameInputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const update = <K extends keyof FormValues>(key: K, value: FormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };
  const isSummaryTask = task.isSummary;
  const requiresConstraintDate = !isSummaryTask && values.constraintType !== 'ASAP' && values.constraintType !== 'ALAP';

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors: Partial<Record<'name' | 'constraintDate' | 'priority' | 'fixedCost', string>> = {};
    const priority = Number(values.priority);
    const fixedCost = Number(values.fixedCost);
    if (!values.name.trim()) nextErrors.name = '請輸入任務名稱。';
    if (requiresConstraintDate && !values.constraintDate) nextErrors.constraintDate = '此限制條件需要日期與時間。';
    if (values.priority.trim() === '' || !Number.isInteger(priority) || priority < 0 || priority > 1000) nextErrors.priority = '優先權必須是 0 到 1000 的整數。';
    if (values.fixedCost.trim() === '' || !Number.isFinite(fixedCost)) nextErrors.fixedCost = '固定成本必須是數字。';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      name: values.name.trim(),
      fixedCost: money(fixedCost),
      fixedCostAccrual: values.fixedCostAccrual,
      notes: values.notes,
      ...(isSummaryTask ? {} : {
        taskType: values.taskType,
        effortDriven: values.effortDriven,
        isManuallyScheduled: values.isManuallyScheduled,
        constraintType: values.constraintType,
        constraintDate: requiresConstraintDate ? fromLocalInput(values.constraintDate) : null,
        deadline: fromLocalInput(values.deadline),
        calendarId: values.calendarId === '' ? null : values.calendarId as Task['calendarId'],
        ignoreResourceCalendar: values.ignoreResourceCalendar,
        priority,
      }),
    });
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form aria-labelledby={titleId} aria-modal="true" className="max-h-full w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900" ref={dialogRef} onKeyDown={trapFocus} onSubmit={handleSubmit} role="dialog">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">任務詳細資料</h2>
          <button type="button" onClick={onCancel} aria-label="關閉任務詳細資料" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>
        {isSummaryTask ? <p role="note" className="mb-4 rounded bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">這是摘要任務；日期與排程行為由子任務彙總，限制條件、期限、日曆、優先權與排程設定不會套用，請改編輯子任務。固定成本仍會納入摘要成本彙總。</p> : null}
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <label className="grid gap-1 sm:col-span-2">任務名稱
            <input ref={nameInputRef} disabled={readOnly} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'task-name-error' : undefined} value={values.name} onChange={(event) => update('name', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {errors.name ? <span id="task-name-error" className="text-xs text-red-600">{errors.name}</span> : null}
          </label>
          <label className="grid gap-1">任務類型
            <select disabled={readOnly || isSummaryTask} value={values.taskType} onChange={(event) => update('taskType', event.target.value as TaskType)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600"><option value="FixedUnits">固定單位</option><option value="FixedDuration">固定工期</option><option value="FixedWork">固定工時</option></select>
          </label>
          <div className="grid content-end gap-2 pb-1">
            <label className="flex items-center gap-2"><input disabled={readOnly || isSummaryTask} type="checkbox" checked={values.effortDriven} onChange={(event) => update('effortDriven', event.target.checked)} /> 工作導向</label>
            <label className="flex items-center gap-2"><input disabled={readOnly || isSummaryTask} type="checkbox" checked={values.isManuallyScheduled} onChange={(event) => update('isManuallyScheduled', event.target.checked)} /> 手動排程</label>
          </div>
          <label className="grid gap-1">限制條件
            <select disabled={readOnly || isSummaryTask} value={values.constraintType} onChange={(event) => { update('constraintType', event.target.value as ConstraintType); setErrors((current) => ({ ...current, constraintDate: undefined })); }} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600">{CONSTRAINT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
          </label>
          <label className="grid gap-1">限制日期與時間{requiresConstraintDate ? '（必填）' : '（不適用）'}
            <input type="datetime-local" disabled={readOnly || !requiresConstraintDate} aria-invalid={Boolean(errors.constraintDate)} aria-describedby={errors.constraintDate ? 'task-constraint-date-error' : undefined} value={values.constraintDate} onChange={(event) => update('constraintDate', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600" />
            {errors.constraintDate ? <span id="task-constraint-date-error" className="text-xs text-red-600">{errors.constraintDate}</span> : null}
          </label>
          <label className="grid gap-1">期限
            <input type="datetime-local" disabled={readOnly || isSummaryTask} value={values.deadline} onChange={(event) => update('deadline', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600" />
          </label>
          <label className="grid gap-1">任務日曆
            <select disabled={readOnly || isSummaryTask} value={values.calendarId} onChange={(event) => update('calendarId', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600"><option value="">使用專案日曆</option>{calendars.map((calendar) => <option key={calendar.id as string} value={calendar.id as string}>{calendar.name}</option>)}</select>
          </label>
          <label className="flex items-center gap-2 self-end pb-2"><input disabled={readOnly || isSummaryTask} type="checkbox" checked={values.ignoreResourceCalendar} onChange={(event) => update('ignoreResourceCalendar', event.target.checked)} /> 排程時忽略資源日曆</label>
          <label className="grid gap-1">優先權（0–1000）
            <input type="number" disabled={readOnly || isSummaryTask} aria-invalid={Boolean(errors.priority)} aria-describedby={errors.priority ? 'task-priority-error' : undefined} value={values.priority} onChange={(event) => update('priority', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600" />
            {errors.priority ? <span id="task-priority-error" className="text-xs text-red-600">{errors.priority}</span> : null}
          </label>
          <label className="grid gap-1">固定成本
            <input type="number" step="any" disabled={readOnly} aria-invalid={Boolean(errors.fixedCost)} aria-describedby={errors.fixedCost ? 'task-fixed-cost-error' : undefined} value={values.fixedCost} onChange={(event) => update('fixedCost', event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
            {errors.fixedCost ? <span id="task-fixed-cost-error" className="text-xs text-red-600">{errors.fixedCost}</span> : null}
          </label>
          <label className="grid gap-1">固定成本累計方式
            <select disabled={readOnly} value={values.fixedCostAccrual} onChange={(event) => update('fixedCostAccrual', event.target.value as Task['fixedCostAccrual'])} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600"><option value="Start">開始</option><option value="Prorated">按比例</option><option value="End">完成</option></select>
          </label>
          <label className="grid gap-1 sm:col-span-2">備註
            <textarea disabled={readOnly} value={values.notes} onChange={(event) => update('notes', event.target.value)} rows={3} className="resize-y rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" />
          </label>
        </div>
        {collaboration === undefined ? null : (
          <section className="mt-5 border-t border-line pt-4">
            <h3 className="mb-3 text-sm font-semibold">協作</h3>
            {/* key 讓換任務時整個面板重掛，不會有一個 frame 閃出上一個任務的留言。 */}
            <TaskCollaborationPanel key={task.id} taskId={task.id} collaboration={collaboration} />
          </section>
        )}
        <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">{readOnly ? "關閉" : "取消"}</button>{readOnly ? null : <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">儲存</button>}</div>
      </form>
    </div>
  );
}
